// Full-screen Google-only auth wall.
// Placed BEFORE the Dashboard renders, so the audit + questionnaire flow stay
// fully anonymous. The wall fires only at the moment we're about to reveal the
// AI score, capturing email at the highest-intent point in the funnel.

import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, ShieldCheck, Mail, Lock } from 'lucide-react';
import { signInWithGoogle } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { track } from '../services/analyticsEvents';
import consoleLogger from '../utils/consoleLogger';

const AuthGate = ({ children }) => {
  const { user, loading } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState(null);
  const wallTrackedRef = useRef(false);
  const signInTrackedRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!user && !wallTrackedRef.current) {
      wallTrackedRef.current = true;
      track.authWallShown();
    }
    if (user && !signInTrackedRef.current) {
      signInTrackedRef.current = true;
      track.authSignedIn(user.email);
      // Tag all subsequent streamed logs with the signed-in email so we can
      // correlate console activity with paying customers.
      if (user.email) consoleLogger.setProfileContext({ userEmail: user.email });
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={spinnerStyle} />
        <p style={{ color: '#64748b', marginTop: 16, fontSize: 14 }}>Loading…</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (user) return children;

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true);
    setError(null);
    try {
      const result = await signInWithGoogle();
      if (!result?.success) {
        setError(result?.error || 'Sign in failed. Please try again.');
      }
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={bgOrb1} />
      <div style={bgOrb2} />

      <div style={scrollerStyle}>
      <div style={cardStyle}>
        <div style={badgeStyle}>
          <Sparkles size={14} />
          <span>Your audit is ready</span>
        </div>

        <h1 style={headlineStyle}>
          Sign in to view your<br />
          <span style={gradientText}>LinkedIn profile audit</span>
        </h1>

        <p style={subStyle}>
          One click with Google. We save your audit so you can come back to it
          anytime, and email you the full report.
        </p>

        <button
          onClick={handleGoogleSignIn}
          disabled={isSigningIn}
          style={{ ...googleBtnStyle, opacity: isSigningIn ? 0.6 : 1, cursor: isSigningIn ? 'wait' : 'pointer' }}
          onMouseEnter={(e) => {
            if (isSigningIn) return;
            e.currentTarget.style.borderColor = '#0ea5e9';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(14,165,233,0.15)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <GoogleIcon />
          <span>{isSigningIn ? 'Opening Google…' : 'Continue with Google'}</span>
        </button>

        {error && <p style={errorStyle}>{error}</p>}

        <div style={trustRowStyle}>
          <div style={trustItemStyle}>
            <ShieldCheck size={14} color="#0ea5e9" />
            <span>Free audit</span>
          </div>
          <div style={trustItemStyle}>
            <Lock size={14} color="#0ea5e9" />
            <span>No card needed</span>
          </div>
          <div style={trustItemStyle}>
            <Mail size={14} color="#0ea5e9" />
            <span>No spam</span>
          </div>
        </div>

        <p style={fineprintStyle}>
          We never post on your behalf or share your email.
        </p>
      </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, -20px); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-25px, 15px); }
        }
      `}</style>
    </div>
  );
};

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 50%, #f5f3ff 100%)',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
};
// Inner wrapper: flex-centers the card vertically + horizontally when it
// fits, and lets the page scroll naturally when the card is taller than the
// viewport (short laptops, mobile landscape, etc.). Without this, the flex
// centering on the fixed overlay pinned the card to the top.
const scrollerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  boxSizing: 'border-box',
  position: 'relative',
};
const bgOrb1 = {
  position: 'absolute',
  top: '-150px',
  right: '-100px',
  width: '380px',
  height: '380px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 70%)',
  pointerEvents: 'none',
  animation: 'float1 12s ease-in-out infinite',
};
const bgOrb2 = {
  position: 'absolute',
  bottom: '-180px',
  left: '-120px',
  width: '420px',
  height: '420px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(139,92,246,0.16) 0%, transparent 70%)',
  pointerEvents: 'none',
  animation: 'float2 14s ease-in-out infinite',
};
const cardStyle = {
  position: 'relative',
  background: 'white',
  borderRadius: '24px',
  padding: '44px 36px 32px',
  maxWidth: '460px',
  width: '100%',
  boxShadow: '0 20px 60px rgba(15,23,42,0.12), 0 8px 24px rgba(15,23,42,0.06)',
  textAlign: 'center',
  animation: 'fadeUp 0.5s ease-out',
  border: '1px solid rgba(226,232,240,0.6)',
};
const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 14px',
  borderRadius: 999,
  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
  color: '#0369a1',
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 22,
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
  border: '1px solid rgba(186,230,253,0.6)',
};
const headlineStyle = {
  fontSize: 26,
  fontWeight: 700,
  color: '#0f172a',
  margin: '0 0 14px',
  lineHeight: 1.25,
  letterSpacing: '-0.01em',
};
const gradientText = {
  background: 'linear-gradient(90deg, #0ea5e9 0%, #8b5cf6 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};
const subStyle = {
  fontSize: 14.5,
  color: '#475569',
  margin: '0 0 28px',
  lineHeight: 1.55,
  maxWidth: 380,
  marginLeft: 'auto',
  marginRight: 'auto',
};
const googleBtnStyle = {
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: '15px 22px',
  background: 'white',
  color: '#0f172a',
  border: '1.5px solid #e2e8f0',
  borderRadius: 14,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  fontFamily: 'inherit',
};
const errorStyle = {
  marginTop: 16,
  padding: '10px 14px',
  background: '#fef2f2',
  color: '#dc2626',
  borderRadius: 10,
  fontSize: 13,
  textAlign: 'left',
};
const trustRowStyle = {
  display: 'flex',
  justifyContent: 'center',
  gap: 18,
  marginTop: 24,
  flexWrap: 'wrap',
};
const trustItemStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12.5,
  color: '#475569',
  fontWeight: 500,
};
const fineprintStyle = {
  marginTop: 18,
  fontSize: 11.5,
  color: '#94a3b8',
  margin: '18px 0 0',
};
const loadingStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};
const spinnerStyle = {
  width: 36,
  height: 36,
  border: '3px solid #e2e8f0',
  borderTopColor: '#0ea5e9',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

export default AuthGate;
